<template>
    <div>
        <div v-loading="m__loading" class="relative">
            <Chart :options="chart_options"></Chart>

            <div
                v-if="show_no_data"
                class="w-full h-full bg-white absolute top-0 left-0"
            >
                <div
                    class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                    <div class="util-flex-center flex-col">
                        <img
                            class="w-[140px] h-[140px]"
                            :src="require('@/assets/images/no_data.png')"
                        />
                        <p class="mt-2 text-desc-text text-[0.9rem]">
                            {{ $t('common.no_data') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-8">
            <div class="mb-4 flex items-center space-x-2">
                <p class="text-[0.9rem] font-semibold">
                    {{ $t('common.detailed_analysis') }}
                </p>

                <div class="!ml-auto"></div>

                <el-button
                    size="small"
                    @click="show_custom_column_dialog = true"
                >
                    {{ $t('button.custom_column') }}
                </el-button>
                <div
                    class="cursor-pointer p-2 rounded-lg bg-bg"
                    @click="handleExportExcel"
                >
                    <el-tooltip
                        class="item"
                        :content="$t(`common.export_excel`)"
                        effect="dark"
                        placement="top"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-4 h-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m.75 12l3 3m0 0l3-3m-3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                            />
                        </svg>
                    </el-tooltip>
                </div>
            </div>

            <el-table
                :data="data_overview_report"
                @sort-change="handleSortChange"
            >
                <el-table-column
                    width="160"
                    :show-overflow-tooltip="true"
                    :label="$t('common.ad')"
                    :fixed="true"
                >
                    <template #default="scope">
                        <p class="truncate w-[200px]">
                            {{ getNameByAdgroupId(scope.row.ad_id) }}
                        </p>
                    </template>
                </el-table-column>

                <el-table-column
                    v-for="(item, index) in selected_metrics_for_table"
                    :key="index"
                    width="160"
                    :show-overflow-tooltip="true"
                    :prop="item"
                    :label="$t(`page.campaign.column_${item}`)"
                    sortable="custom"
                    align="right"
                >
                    <template v-slot:header>
                        <el-tooltip
                            class="item"
                            :content="$t(`page.campaign.column_${item}`)"
                            effect="dark"
                            placement="top"
                        >
                            <div slot="content" class="max-w-[360px]">
                                <p
                                    class="text-[0.85rem]"
                                    v-html="$t(`page.campaign.column_${item}`)"
                                ></p>

                                <p
                                    v-if="
                                        $t(
                                            `page.campaign.description_column_${item}`
                                        ) !==
                                        `page.campaign.description_column_${item}`
                                    "
                                    class="mt-1"
                                    v-html="
                                        $t(
                                            `page.campaign.description_column_${item}`
                                        )
                                    "
                                ></p>
                            </div>

                            <p>
                                {{ $t(`page.campaign.column_${item}`) }}
                            </p>
                        </el-tooltip>
                    </template>

                    <template #default="scope">
                        <p>
                            {{
                                m__formatterColumn(
                                    scope.row[item],
                                    item,
                                    currency
                                )
                            }}
                        </p>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <CustomColumnDialog
            :visible.sync="show_custom_column_dialog"
            :column_values.sync="selected_metrics_for_table"
        ></CustomColumnDialog>
    </div>
</template>

<script>
import {
    getReportFromTTA,
    exportExcel
} from '@/services/atosa-tiktok-ads/reporting'
import { Chart } from 'highcharts-vue'
import campaignMixin from '@/views/campaign/_mixin'
import moment from 'moment'
import CustomColumnDialog from '@/views/campaign/_components/view-data-drawer/_components/custom-column-dialog'

const renderYAxis = () => {
    return {
        title: {
            text: ''
        },

        visible: true
    }
}

export default {
    components: {
        Chart,
        CustomColumnDialog
    },

    mixins: [campaignMixin],

    props: [
        'tiktok_account_id',
        'advertiser_id',
        'ranger_date',
        'selected_metrics',
        'currency',
        'stat_time',
        'data_report_ads',
        'data_id'
    ],

    data() {
        return {
            selected_metrics_for_table: [],
            show_custom_column_dialog: false,
            chart: [],
            data_overview_report: [],
            order_field: 'spend',
            order_type: 'DESC',
            chart_options: {
                chart: {
                    type: 'column',
                    height: 260
                },
                title: {
                    text: ''
                },
                legend: {
                    enabled: false,
                    floating: true
                },
                xAxis: {
                    categories: [],
                    crosshair: true,
                    minTickInterval: 6,
                    tickInterval: 1,
                    endOnTick: true,
                    startOnTick: true
                },
                tooltip: {
                    shared: true
                },
                yAxis: [],
                series: []
            }
        }
    },

    computed: {
        metrics() {
            return this.trend_chart_options.map((item) => item.value)
        },

        trend_chart_options() {
            return this.p__trend_chart_options_2.map((item) => ({
                ...item,
                yAxis: renderYAxis()
            }))
        },

        show_no_data() {
            if (!this.chart.length) {
                return true
            }

            return false
        }
    },

    watch: {
        selected_metrics() {
            this.changeSeriesChart()
        },

        data_id() {
            this.fetchOverviewReport(true)
        },

        stat_time() {
            this.fetchOverviewReport(true)
        },

        ranger_date() {
            this.fetchOverviewReport(true)
        }
    },

    mounted() {
        this.fetchOverviewReport()

        const m__formatterColumn = (x, y) =>
            this.m__formatterColumn(x, y, this.currency)
        const p__getNameByTrend = this.p__getNameByTrend
        this.chart_options.tooltip.pointFormatter = function () {
            const point = this
            const series = point.series
            const y = m__formatterColumn(point.y || 0, series.name)
            return `${p__getNameByTrend(series.name)}: <b>${y}</b><br/>`
        }

        this.selected_metrics_for_table = this.p__trend_basic_chart_options.map(
            (item) => item.value
        )
    },

    methods: {
        async fetchOverviewReport(reset_sort = false) {
            if (!this.data_id) {
                return
            }

            this.m__loading = true

            try {
                if (reset_sort) {
                    this.order_field = 'spend'
                    this.order_type = 'DESC'
                }

                const filtering = [
                    {
                        filter_value: JSON.stringify([this.data_id]),
                        field_name: 'adgroup_ids',
                        filter_type: 'IN'
                    }
                ]

                const response = await getReportFromTTA(
                    this.tiktok_account_id,
                    {
                        page_size: 50,
                        page: 1,
                        filtering: JSON.stringify(filtering),
                        advertiser_id: this.advertiser_id,
                        start_date: this.ranger_date[0],
                        end_date: this.ranger_date[1],
                        type: 'ad_id',
                        report_type: 'BASIC',
                        order_field: this.order_field,
                        order_type: this.order_type,
                        metrics: JSON.stringify(this.metrics),
                        dimensions: JSON.stringify([
                            'ad_id',
                            // 'country_code',
                            this.stat_time
                        ]),
                        data_level: 'AUCTION_AD'
                    }
                )

                let temp = response.data.list.map((item) => ({
                    ...item.dimensions,
                    ...item.metrics
                }))

                this.chart = temp

                const categories = this.chart
                    .map((item) =>
                        this.p__formatDate(
                            item.stat_time_day || item.stat_time_hour,
                            item.stat_time_day
                                ? this.$t('common.format_date')
                                : this.$t('common.format_fulldate')
                        )
                    )
                    .sort((a, b) =>
                        moment(moment(a, this.$t('common.format_date'))).diff(
                            moment(b, this.$t('common.format_date'))
                        )
                    )

                this.chart_options.xAxis.categories = categories
                this.changeSeriesChart()

                temp = temp.reduce((acc, current) => {
                    const item = acc.find(
                        (i) => i.placement === current.placement
                    )
                    if (item) {
                        this.metrics.forEach((m) => {
                            item[m] =
                                parseFloat(item[m]) + parseFloat(current[m])
                        })
                    } else {
                        acc.push(current)
                    }

                    return acc
                }, [])

                temp.forEach((item) => {
                    this.metrics.forEach((m) => {
                        if (m === 'cpc') {
                            item[m] = item['spend'] / item['clicks']
                        }
                        if (m === 'cpm') {
                            item[m] = item['spend'] / item['impressions']
                        }
                        if (m === 'cost_per_conversion') {
                            if (item['conversion'] === 0) {
                                item[m] = 0
                            } else {
                                item[m] = item['spend'] / item['conversion']
                            }
                        }
                    })
                })

                this.data_overview_report = temp
            } catch (error) {
                console.error(error)
            }

            this.m__loading = false
        },

        async handleSortChange({ column, prop, order }) {
            this.order_field = prop
            this.order_type = order === 'descending' ? 'DESC' : 'ASC'

            this.fetchOverviewReport()
        },

        changeSeriesChart() {
            const series = []
            const yAxis = []
            let index = 0

            for (const item of this.selected_metrics) {
                const data = this.chart.map((it) => +it[item.value])

                series.push({
                    type: 'spline',
                    name: item.value,
                    color: item.color,
                    data,
                    yAxis: index,
                    marker: {
                        enabled: false,
                        states: {
                            hover: {
                                enabled: false
                            }
                        }
                    }
                })

                yAxis.push({
                    title: {
                        text: ''
                    },
                    opposite: true,
                    labels: {
                        enabled: false
                    }
                })

                index++
            }

            this.chart_options.series = series
            this.chart_options.yAxis = yAxis
        },

        getNameByAdgroupId(ad_id) {
            if (this.data_report_ads.length > 0) {
                const item = this.data_report_ads.find(
                    (item) => item.ad.ad_id === ad_id
                )

                if (item) {
                    return item.ad.ad_name
                }
            }

            return ad_id
        },

        async handleExportExcel() {
            try {
                const filtering = [
                    {
                        filter_value: JSON.stringify([this.data_id]),
                        field_name: 'adgroup_ids',
                        filter_type: 'IN'
                    }
                ]

                const response = await exportExcel(this.tiktok_account_id, {
                    page_size: 50,
                    page: 1,
                    filtering: JSON.stringify(filtering),
                    advertiser_id: this.advertiser_id,
                    start_date: this.ranger_date[0],
                    end_date: this.ranger_date[1],
                    type: 'ad_id',
                    report_type: 'BASIC',
                    order_field: this.order_field,
                    order_type: this.order_type,
                    metrics: JSON.stringify(this.metrics),
                    dimensions: JSON.stringify([
                        'ad_id',
                        // 'country_code',
                        this.stat_time
                    ]),
                    data_level: 'AUCTION_AD'
                })

                const date = moment().format('HHmmDDMMyyyy')

                const filename = `excel-ad-${date}`

                this.p__exportExcelByBlob(response, filename)
            } catch (error) {
                console.error(error)
            }
        }
    }
}
</script>
